import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import loadable from '@loadable/component';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleHeader
} from '../../../components/Collapsible';
import DataTable from '../../../components/DataTable';
import DescriptionBox from '../../../components/DescriptionBox';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  setCollapseContentTracking,
  addLead,
  setGenericEvent
} from '../../../store/actions/dataLayer';
import { createSearchAlert } from '../../../store/actions';
import {
  getTitle,
  getDetailsData,
  getBoatDetails,
  getPropulsionData,
  getMeasurementsData,
  getFeaturesData,
  formatMoreDetailsDescriptions,
  getLocation,
  isFSBOContact,
  getSavedSearchDetailsData,
  getSavedSearchDetailsSearchPath
} from '../../../utils/listingHelper';
import { getConfig } from '../../../config/portal';
import { injectIntl } from 'react-intl';
import messages from '../../../utils/translationMessages';
import { listingPropTypes } from '../../../utils/commonPropTypes';
import { BREAKPOINTS } from '../../../utils/commonHelper';
import { isManufacturerListing } from '../../../utils/commonHelper';
import ContactGroup from '../ContactGroup';
import ServicesByDFP from '../ServicesByDFP';
import Services from '../Services';
import DealerOtherProducts from '../DealerOtherProducts';
import { Ad } from '@dmm/react-common-components';
import { PortalConfigContext } from '../../../config/portal';
import { YoutubeVideos } from '@dmm/lib-react-videos';
import '@dmm/lib-react-videos/dist/@dmm/lib-react-videos.min.css';
import SaveSearchFromDetails from '../../../components/SaveSearchFromDetails';
import ConversationalCta from '../../../components/ConversationalCta';
import SimilarListings from '../SimilarListings';
import RelatedArticles from '../RelatedArticles';
import { BoatDetails } from '@dmm/lib-react-ui-components';
import { isBrokerBDP } from '../../../utils/newBDPHelper';

/* Loadable Facets */
const LocationMap = loadable(() => import('../LocationMap'));

class DetailsSection extends PureComponent {
  render() {
    const {
      breakpoint,
      listing,
      isLoading,
      openLocationCounter,
      openLocationSection,
      setCollapseContentTracking,
      onOpenContactForm,
      intl: { formatMessage: t },
      adParams,
      customUom
    } = this.props;
    const config = getConfig();
    const portal = get(this.context, 'name');
    const title = getTitle(listing);
    const locationMapOpenState =
      openLocationSection === 'location-map' ? 'open' : 'close';
    const dealerListings = listing.dealerListings;
    const propulsionData = getPropulsionData(listing);
    const measurementsData = getMeasurementsData(listing, customUom);
    const featuresData = getFeaturesData(listing);
    const moreDetails = formatMoreDetailsDescriptions(listing.descriptions);
    const coordinatesBoat = get(listing, 'location.coordinates', {});
    const address = getLocation(listing);
    const website = get(listing, 'contact.website');
    const isFSBOSeller = isFSBOContact(listing.contact);
    const isManufacturer = isManufacturerListing(listing.isOemModel);
    const BDPContactFormModal = get(
      this.context,
      'supports.BDPContactFormModal',
      false
    );
    const servicesItems = get(this.context, 'services.items', []);
    const servicesItemsByDFP = get(this.context, 'services.itemsByDFP', []);
    const supportsServices = get(this.context, 'supports.services', false);
    const videos = get(listing, 'videos', []);
    const supportsSavedSearchOnBDP = get(
      this.context,
      'supports.savedSearchOnBDP',
      false
    );
    const supportsRelatedArticulesOnBDP = get(
      this.context,
      'supports.relatedArticles',
      true
    );
    const supportsConversationalCta = get(
      config,
      'supports.bdp.conversationalCta',
      false
    );
    const similarListings = get(listing, 'similarBoats.records.records', []);
    const relatedArticles = get(listing, 'relatedArticles', []);
    const newDesignActive = isBrokerBDP(listing, this.context);

    return (
      <div className="details">
        {newDesignActive && (
          <BoatDetails details={getBoatDetails(listing, customUom)} />
        )}
        {!newDesignActive && (
          <CollapsibleContent initialState="open" classOverride="details">
            <CollapsibleHeader
              id="boat-details"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('boat-details');
                }
              }}
            >
              {t(messages.details)}
            </CollapsibleHeader>
            <Collapsible type="no-pad">
              <DataTable
                dataName="details"
                data={getDetailsData(listing, customUom)}
                isLoading={isLoading}
              />
            </Collapsible>
          </CollapsibleContent>
        )}

        <CollapsibleContent initialState="open" classOverride="details">
          <CollapsibleHeader
            id="boat-description"
            onClick={(e, open) => {
              if (open) {
                setCollapseContentTracking('boat-description');
              }
            }}
          >
            {t(messages.description)}
          </CollapsibleHeader>
          <Collapsible type="no-pad">
            <span>{title}</span>
            <DescriptionBox
              dataName="description"
              showMore={true}
              data={get(listing, 'description', '')}
              isLoading={isLoading}
              tracking={setCollapseContentTracking}
            />
          </Collapsible>
          <ConversationalCta
            id="description-conversational-cta"
            enabled={supportsConversationalCta}
            variant="button"
            classNames={{ icon: true }}
            icon="description"
            onClick={onOpenContactForm}
            title={t(messages.boatDetails.description.conversationalCta.title)}
            content={t(
              messages.boatDetails.description.conversationalCta.content
            )}
          />
        </CollapsibleContent>
        <CollapsibleContent initialState="closed" classOverride="details">
          <CollapsibleHeader
            id="measurements"
            onClick={(e, open) => {
              if (open) {
                setCollapseContentTracking('measurements');
              }
            }}
          >
            {t(messages.measurements)}
          </CollapsibleHeader>
          <Collapsible type="no-pad">
            <DataTable
              dataName="measurements"
              data={measurementsData}
              isLoading={isLoading}
            />
          </Collapsible>
          <ConversationalCta
            id="measurements-conversational-cta"
            enabled={supportsConversationalCta}
            variant="button"
            classNames={{ icon: true }}
            icon="measurements"
            onClick={(event) =>
              onOpenContactForm(event, false, {
                prePopulatedText: t(
                  messages.boatDetails.measurements.conversationalCta
                    .prePopulatedText,
                  { portal: config.fullName }
                )
              })
            }
            title={t(messages.boatDetails.measurements.conversationalCta.title)}
            content={t(
              messages.boatDetails.measurements.conversationalCta.content
            )}
          />
        </CollapsibleContent>

        {propulsionData.length > 0 ? (
          <CollapsibleContent initialState="closed" classOverride="details">
            <CollapsibleHeader
              id="propulsion"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('propulsion');
                }
              }}
            >
              {t(messages.propulsion)}
            </CollapsibleHeader>
            <Collapsible type="no-pad">
              <DataTable
                dataName="propulsion"
                data={propulsionData}
                isLoading={isLoading}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {featuresData.length > 0 ? (
          <CollapsibleContent initialState="closed" classOverride="details">
            <CollapsibleHeader
              id="features"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('features');
                }
              }}
            >
              {t(messages.features)}
            </CollapsibleHeader>
            <Collapsible type="no-pad">
              <DataTable
                dataName="features"
                data={featuresData}
                isLoading={isLoading}
              />
            </Collapsible>
            <ConversationalCta
              id="features-conversational-cta"
              enabled={supportsConversationalCta}
              variant="button"
              classNames={{ icon: true }}
              icon="description"
              onClick={(event) =>
                onOpenContactForm(event, false, {
                  prePopulatedText: t(
                    messages.boatDetails.features.conversationalCta
                      .prePopulatedText,
                    { portal: config.fullName }
                  )
                })
              }
              title={t(messages.boatDetails.features.conversationalCta.title)}
              content={t(
                messages.boatDetails.features.conversationalCta.content
              )}
            />
          </CollapsibleContent>
        ) : null}
        {moreDetails && moreDetails.length > 0 ? (
          <CollapsibleContent initialState="closed" classOverride="details">
            <CollapsibleHeader
              id="more-details"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('more-details');
                }
              }}
            >
              {t(messages.moreDetails)}
            </CollapsibleHeader>
            <Collapsible type="no-pad">
              <DescriptionBox
                dataName="more-details"
                showMore={false}
                multiItem={true}
                data={moreDetails}
                isLoading={isLoading}
              />
            </Collapsible>
            <ConversationalCta
              id="more-details-conversational-cta"
              enabled={supportsConversationalCta}
              variant="button"
              classNames={{ icon: true }}
              icon="description"
              onClick={onOpenContactForm}
              title={t(
                messages.boatDetails.moreDetails.conversationalCta.title
              )}
              content={t(
                messages.boatDetails.moreDetails.conversationalCta.content
              )}
            />
          </CollapsibleContent>
        ) : null}
        {(breakpoint !== BREAKPOINTS.desktop || BDPContactFormModal) && (
          <ContactGroup
            mode={ContactGroup.MODES.inline}
            breakpoint={breakpoint}
            onOpenContact={onOpenContactForm}
            listing={listing}
            hidden={BDPContactFormModal}
          />
        )}

        {breakpoint !== BREAKPOINTS.desktop && (
          <div className="tablet-ads-wrapper">
            <Ad {...adParams.mobileBox1Params} />
            {breakpoint === BREAKPOINTS.tablet && (
              <Ad {...adParams.mobileBox2Params} />
            )}
          </div>
        )}

        {!isFSBOSeller && dealerListings && dealerListings.length > 0 ? (
          <CollapsibleContent
            initialState="open"
            classOverride="details more-from-dealer-collapsible"
          >
            <CollapsibleHeader
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('dealerlistings');
                }
              }}
            >
              {isManufacturer
                ? t(messages.moreFromManufacturer)
                : t(messages.moreFromDealer)}
            </CollapsibleHeader>
            <Collapsible type="no-pad">
              <DealerOtherProducts
                dealerListings={dealerListings}
                isLoading={this.props.isWorking}
                listingId={get(listing, 'id')}
                website={website}
                listing={listing}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}
        {supportsServices ? (
          servicesItemsByDFP && servicesItemsByDFP.length > 0 ? (
            <ServicesByDFP services={servicesItemsByDFP} adParams={adParams} />
          ) : (
            servicesItems && servicesItems.length > 0 && <Services />
          )
        ) : null}

        <ConversationalCta
          id="more-questions-conversational-cta"
          enabled={supportsConversationalCta}
          variant="simple"
          title={t(messages.boatDetails.moreQuestions.conversationalCta.title)}
          link={{
            text: t(
              messages.boatDetails.moreQuestions.conversationalCta.linkText
            ),
            onClick: (event) =>
              onOpenContactForm(event, false, {
                prePopulatedText: t(
                  messages.boatDetails.moreQuestions.conversationalCta
                    .prePopulatedText,
                  { portal: config.fullName }
                )
              })
          }}
          classNames={{ untruncate: true }}
        >
          {t(
            messages.boatDetails.moreQuestions.conversationalCta.body.firstRow
          )}
          <br />
          {t(
            messages.boatDetails.moreQuestions.conversationalCta.body.secondRow
          )}
        </ConversationalCta>

        {videos && videos.length > 0 ? (
          <CollapsibleContent initialState="open" classOverride="details">
            <CollapsibleHeader>{t(messages.videoDetails)}</CollapsibleHeader>
            <Collapsible type="no-pad">
              <YoutubeVideos
                videos={videos}
                portal={portal}
                device={breakpoint}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}

        {!isEmpty(coordinatesBoat) && address && !isFSBOSeller && (
          <LocationMap
            open={locationMapOpenState}
            counter={openLocationCounter}
            listing={listing}
            isLoading={isLoading}
            setCollapseContentTracking={this.props.setCollapseContentTracking}
            addLead={this.props.addLead}
            pageType={this.props.pageType}
          />
        )}

        {/* Similar Boats Section */}
        {Array.isArray(similarListings) && similarListings.length > 0 ? (
          <CollapsibleContent
            initialState="open"
            classOverride="details more-from-dealer-collapsible"
          >
            <CollapsibleHeader
              id="similar-boats"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('similarlistings');
                }
              }}
            >
              {t(messages.additionalLeads.similarBoats)}
            </CollapsibleHeader>
            <Collapsible type="no-pad">
              <SimilarListings
                similarListings={similarListings}
                isLoading={this.props.isWorking}
                listingId={get(listing, 'id')}
                listing={listing}
              />
            </Collapsible>
          </CollapsibleContent>
        ) : null}

        {/* Saved search section */}
        {supportsSavedSearchOnBDP && (
          <CollapsibleContent initialState="open" classOverride="details">
            <CollapsibleHeader
              id="boat-save-search"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('boat-save-search');
                }
              }}
            >
              {t(messages.searchAlerts)}
            </CollapsibleHeader>

            <Collapsible type="no-pad">
              <SaveSearchFromDetails
                data={getSavedSearchDetailsData(listing)}
                searchPath={getSavedSearchDetailsSearchPath(listing)}
                createSearchAlert={this.props.createSearchAlert}
                isLoading={isLoading}
                tracking={{
                  setGenericEvent: this.props.setGenericEvent,
                  submitEvent: 'SaveSearchSubscribed'
                }}
                listingId={listing.id}
                portal={portal}
              />
            </Collapsible>
          </CollapsibleContent>
        )}

        {/* Related articles section */}
        {supportsRelatedArticulesOnBDP && (
          <CollapsibleContent initialState="open" classOverride="details">
            <CollapsibleHeader
              id="boat-related-articles"
              onClick={(e, open) => {
                if (open) {
                  setCollapseContentTracking('boat-related-articles');
                }
              }}
            >
              {t(messages.relatedArticles)}
            </CollapsibleHeader>

            <Collapsible type="no-pad">
              <RelatedArticles
                isLoading={isLoading}
                articles={relatedArticles}
              ></RelatedArticles>
            </Collapsible>
          </CollapsibleContent>
        )}
      </div>
    );
  }
}

DetailsSection.propTypes = {
  breakpoint: PropTypes.oneOf(Object.keys(BREAKPOINTS)),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  isLoading: PropTypes.bool,
  isWorking: PropTypes.bool,
  /** Listing information of the boat */
  listing: listingPropTypes.isRequired,
  onOpenContactForm: PropTypes.func,
  setCollapseContentTracking: PropTypes.func.isRequired,
  openLocationCounter: PropTypes.number,
  openLocationSection: PropTypes.string,
  addLead: PropTypes.func.isRequired,
  adParams: PropTypes.object.isRequired,
  customUom: PropTypes.shape({
    length: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    weight: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    speed: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    capacity: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    distance: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    }),
    radius: PropTypes.shape({
      name: PropTypes.string,
      abbr: PropTypes.string.isRequired,
      symbol: PropTypes.string
    })
  }),
  createSearchAlert: PropTypes.func.isRequired,
  setGenericEvent: PropTypes.func.isRequired,
  pageType: PropTypes.string
};

DetailsSection.contextType = PortalConfigContext;

export default connect(null, (dispatch) =>
    bindActionCreators(
      {
        setCollapseContentTracking,
        addLead,
        createSearchAlert,
        setGenericEvent
      },
      dispatch
    )
  )(injectIntl(DetailsSection));
